// @ts-nocheck
import { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Text,
  Flex,
  IconButton,
  Button,
  Center,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Icon,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { useTable, useSortBy, useFilters, useBlockLayout } from "react-table";
import moment from "moment";
import PuffLoader from "react-spinners/PuffLoader";
import { CSVLink } from "react-csv";
import numbro from "numbro";
import { PreviewText } from "components/preview-text";
import {
  NumberRangeFilter,
  PreviewTextFilterUI,
  previewTextFilter,
} from "components/react-table/filters";
import { layerStyle, textStyle, variant } from "theme/names";
import { ellipsisIcon } from "components/icons/ellipsis";
import { TagIcon } from "components/icons/tagIcon";

let maxImpression = 0;
let maxEngagement = 0;
let maxEngagementPercent = 0;
let maxProfileVisits = 0;
let maxLikes = 0;
let maxReplies = 0;
let maxRetweets = 0;
let maxQuoteRetweets = 0;

export function TweetPerformance({
  stats: tweetStats = [],
  isLoading = false,
}) {
  const [stats, setStats] = useState<any[]>([]);
  const [isThreadOnly, setIsThreadOnly] = useState<boolean>(false);
  const csvLink = useRef(null);
  const impressionsRef = useRef(null);
  const engagementRef = useRef(null);
  const engagementPercentRef = useRef(null);
  const profileVisitRef = useRef(null);
  const likesRef = useRef(null);
  const repliesRef = useRef(null);
  const retweetsRef = useRef(null);
  const quoteRtRef = useRef(null);
  const previewTextRef = useRef(null);

  useEffect(() => {
    setStats([...tweetStats]);
  }, [tweetStats]);

  const data = useMemo(() => {
    maxImpression = 0;
    maxEngagement = 0;
    maxEngagementPercent = 0;
    maxProfileVisits = 0;
    maxLikes = 0;
    maxReplies = 0;
    maxRetweets = 0;
    maxQuoteRetweets = 0;
    return stats.map((stat) => {
      const engagementPercent =
        (stat.public_metrics.total_engagement * 100) /
        stat.non_public_metrics.impression_count;
      const createdAt =
        stat.created_at instanceof Date
          ? moment(stat.created_at).format("MMM. Do ’YY")
          : moment(stat.created_at.toDate()).format("MMM. Do ’YY");

      if (stat.non_public_metrics.impression_count > maxImpression)
        maxImpression = stat.non_public_metrics.impression_count;
      if (stat.public_metrics.total_engagement > maxEngagement)
        maxEngagement = stat.public_metrics.total_engagement;
      if (engagementPercent > maxEngagementPercent)
        maxEngagementPercent = engagementPercent;
      if (stat.non_public_metrics.user_profile_clicks > maxProfileVisits)
        maxProfileVisits = stat.non_public_metrics.user_profile_clicks;
      if (stat.public_metrics.like_count > maxLikes)
        maxLikes = stat.public_metrics.like_count;
      if (stat.public_metrics.reply_count > maxReplies)
        maxReplies = stat.public_metrics.reply_count;
      if (stat.public_metrics.retweet_count > maxRetweets)
        maxRetweets = stat.public_metrics.retweet_count;
      if (stat.public_metrics.quote_count > maxQuoteRetweets)
        maxQuoteRetweets = stat.public_metrics.quote_count;

      return {
        date: createdAt,
        previewText: {
          text: stat.text,
          tweet: stat,
          textEscaped: stat.full_text.replace(/"/g, '""'),
        },
        impression: stat.non_public_metrics.impression_count,
        engagement: stat.public_metrics.total_engagement,
        profileVisits: stat.non_public_metrics.user_profile_clicks,
        likes: stat.public_metrics.like_count,
        replies: stat.public_metrics.reply_count,
        retweets: stat.public_metrics.retweet_count,
        quoteRetweets: stat.public_metrics.quote_count,
        engagementPercent: engagementPercent,
      };
    });
  }, [stats]);

  const columns = useMemo(
    () => [
      {
        Header: <Flex {...{ gap: 2, alignItems: "center", mr: 2 }}>Date</Flex>,
        accessor: "date",
        tooltipText: "Date",
        sortDescFirst: true,
        disableFilters: true,
        width: 105,
        Cell: ({ value }) => (
          <Flex alignItems="center" h="100%">
            <Text
              w="100px"
              textAlign="left"
              minW="75px"
              textStyle="body.medium.light"
            >
              {value}
            </Text>
          </Flex>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2, alignItems: "center" }}>
            Text & labels
            {TagIcon}
          </Flex>
        ),
        accessor: "previewText",
        width: 260,
        tooltipText: "Text & labels",
        disableSortBy: true,
        Filter: (props) => (
          <PreviewTextFilterUI {...props} ref={previewTextRef} />
        ),
        filterRef: previewTextRef,
        filter: previewTextFilter,
        Cell: ({ value }) => <PreviewText value={value} asModal showThreads />,
      },
      {
        Header: (
          <Flex {...{ gap: 2 }}>Impress.</Flex>
        ),
        accessor: "impression",
        tooltipText: "Impressions",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => (
          <NumberRangeFilter {...props} ref={impressionsRef} />
        ),
        filterRef: impressionsRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text fontWeight={value && maxImpression == value ? "700" : "500"}>
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2 }}>
            Engag.
          </Flex>
        ),
        accessor: "engagement",
        tooltipText: "Engagement",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={engagementRef} />,
        filterRef: engagementRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxEngagement == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2 }}>
            Engag.%
          </Flex>
        ),
        accessor: "engagementPercent",
        tooltipText: "Engagement Percentage",
        sortDescFirst: true,
        width: 90,
        Filter: (props) => (
          <NumberRangeFilter {...props} ref={engagementPercentRef} />
        ),
        filterRef: engagementPercentRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxEngagementPercent == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {value.toFixed(2) + "%"}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 0, alignItems: "center", flex: 1, width: "50px" }}>
            Pro. Vis.
          </Flex>
        ),
        accessor: "profileVisits",
        tooltipText: "Profile Visits",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => (
          <NumberRangeFilter {...props} ref={profileVisitRef} />
        ),
        filterRef: profileVisitRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxProfileVisits == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2, alignItems: "center" }}>Likes</Flex>
        ),
        accessor: "likes",
        tooltipText: "Likes",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={likesRef} />,
        filterRef: likesRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxLikes == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2, alignItems: "center" }}>Replies</Flex>
        ),
        accessor: "replies",
        tooltipText: "Replies",
        isNumeric: "true",
        sortDescFirst: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={repliesRef} />,
        filterRef: repliesRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxReplies == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 2, alignItems: "center" }}>Rt</Flex>
        ),
        accessor: "retweets",
        tooltipText: "Retweets",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={retweetsRef} />,
        filterRef: retweetsRef,
        filter: "between",
        width: 60,
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxRetweets == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: (
          <Flex {...{ gap: 0, alignItems: "center", width: "30px" }}>
            Q' Rt
          </Flex>
        ),
        accessor: "quoteRetweets",
        tooltipText: "Quote Retweets",
        isNumeric: true,
        sortDescFirst: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={quoteRtRef} />,
        filterRef: quoteRtRef,
        filter: "between",
        Cell: ({ value }) => (
          <Text
            fontWeight={value && maxQuoteRetweets == value ? "700" : "500"}
            textStyle="body.medium.light"
          >
            {numbro(value).format({ thousandSeparated: true })}
          </Text>
        ),
      },
    ],
    [],
  );

  const headers = columns
    ?.map((x) => {
      return { label: x.tooltipText, key: x.accessor };
    })
    .filter((x) => x.key !== "ai" && x.key !== "previewText");
  headers.push({ label: "Tweet", key: "previewText.textEscaped" });

  const handleThreadsOnly = () => {
    const previewTextFilters =
      state?.filters?.find((l) => l.id === "previewText")?.value ?? {};

    setIsThreadOnly(!isThreadOnly);
    setFilter("previewText", {
      ...previewTextFilters,
      isThreadsOnly: !isThreadOnly,
    });
  };

  const defaultColumn = useMemo(
    () => ({
      width: 80,
    }),
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setFilter,
    setAllFilters,
  } = useTable(
    { columns, data, defaultColumn },
    useFilters,
    useSortBy,
    useBlockLayout,
  );

  return (
    <Box>
      <Text textStyle={textStyle["h2.bold.standard"]} mb="4">
        Detailed tweet performance
      </Text>
      <Flex justifyContent="flex-end">
        <Button
          variant={variant.Button.lightSelected}
          borderRadius="10px"
          onClick={handleThreadsOnly}
          variant={
            isThreadOnly
              ? variant.Button.secondarySelected
              : variant.Button.secondary
          }
          leftIcon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 5.5V4.5H12.5V5.5H2.5ZM1.25 1.5C0.8125 1.5 0.5 1.1875 0.5 0.75C0.5 0.34375 0.8125 0 1.25 0H13.75C14.1562 0 14.5 0.34375 14.5 0.75C14.5 1.1875 14.1562 1.5 13.75 1.5H12.5V3.5H2.5V1.5H1.25ZM4 1.5V2.5H11V1.5H4ZM12.5 8.5V9.5H2.5V8.5H12.5ZM14 6.5C14.25 6.5 14.5 6.75 14.5 7C14.5 7.28125 14.25 7.5 14 7.5H2.5V6.5H14ZM13.75 14.5C14.1562 14.5 14.5 14.8438 14.5 15.25C14.5 15.6875 14.1562 16 13.75 16H1.25C0.8125 16 0.5 15.6875 0.5 15.25C0.5 14.8438 0.8125 14.5 1.25 14.5H2.5V12.5H12.5V14.5H13.75ZM11 14.5V13.5H4V14.5H11ZM2.5 11.5V10.5H12.5V11.5H2.5Z"
                fill="currentColor"
              />
            </svg>
          }
        >
          Threads only
        </Button>
        <Button
          ml="2"
          onClick={() => {
            setAllFilters([]);
            setIsThreadOnly(false);
          }}
          variant={"secondary"}
          leftIcon={
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 1.75V6.75C15.5 7.1875 15.1562 7.5 14.75 7.5H9.75C9.3125 7.5 9 7.1875 9 6.75C9 6.34375 9.3125 6 9.75 6H12.875C11.9375 4.21875 10.0625 3 8 3C4.9375 3 2.5 5.46875 2.5 8.5C2.5 11.5625 4.9375 14 8 14C9.28125 14 10.5312 13.5625 11.5 12.75C11.8125 12.4688 12.2812 12.5312 12.5625 12.8438C12.8125 13.1562 12.7812 13.625 12.4688 13.9062C11.2188 14.9375 9.625 15.5 7.96875 15.5C4.125 15.5 1 12.375 1 8.5C1 4.65625 4.125 1.53125 7.96875 1.53125C10.4688 1.53125 12.75 2.875 14 4.9375V1.75C14 1.34375 14.3125 1 14.75 1C15.1562 1 15.5 1.34375 15.5 1.75Z"
                fill="currentColor"
              />
            </svg>
          }
        >
          Reset Filters
        </Button>
        <Menu variant="secondary">
          <MenuButton
            as={IconButton}
            variant="secondary"
            ml={2}
            icon={ellipsisIcon}
          />
          <MenuList>
            <MenuItem>
              <CSVLink
                data={data}
                headers={headers}
                filename={"tweet_stats.csv"}
                className="hidden"
                ref={csvLink}
                target="_blank"
              >
                Export CSV
              </CSVLink>
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      <Box
        mt="2"
        layerStyle={layerStyle["bg.border.rounded"]}
        p="4"
        maxWidth="100%"
        overflowX="scroll"
        position="relative"
      >
        <Table {...getTableProps()} fontSize="xs">
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({ title: undefined }),
                    )}
                    px="0"
                    pl={
                      column.tooltipText === "Date" ||
                        column.tooltipText === "Text & labels"
                        ? "0"
                        : "0"
                    }
                    fontSize="2xs"
                  >
                    <Flex
                      alignItems="center"
                      justifyContent={
                        column.id === "date" || column.id === "previewText"
                          ? "start"
                          : "center"
                      }
                    >
                      <Box
                        {...column.getSortByToggleProps({ title: undefined })}
                      >
                        <Tooltip label={column.tooltipText} key={index}>
                          {column.render("Header")}
                        </Tooltip>
                      </Box>
                      {column.canFilter ? (
                        <Popover initialFocusRef={column.filterRef}>
                          <PopoverTrigger>
                            <Button
                              variant="action"
                              size="xs"
                              px="0"
                              cursor="pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Tooltip label={`Filter ${column.tooltipText}`}>
                                <chakra.span>
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 14 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M0 2.75C0 2.34375 0.3125 2 0.75 2H13.25C13.6562 2 14 2.34375 14 2.75C14 3.1875 13.6562 3.5 13.25 3.5H0.75C0.3125 3.5 0 3.1875 0 2.75ZM2 7.75C2 7.34375 2.3125 7 2.75 7H11.25C11.6562 7 12 7.34375 12 7.75C12 8.1875 11.6562 8.5 11.25 8.5H2.75C2.3125 8.5 2 8.1875 2 7.75ZM8.25 13.5H5.75C5.3125 13.5 5 13.1875 5 12.75C5 12.3438 5.3125 12 5.75 12H8.25C8.65625 12 9 12.3438 9 12.75C9 13.1875 8.65625 13.5 8.25 13.5Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </chakra.span>
                              </Tooltip>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody p="4" _focus={{ boxShadow: "lg" }}>
                              {column.render("Filter")}
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      ) : null}
                      {column.isSorted ? (
                        <chakra.span>
                          {column.isSortedDesc ? (
                            <Icon
                              as={TriangleDownIcon}
                              aria-label="sorted descending"
                              color="gray.400"
                            />
                          ) : (
                            <Icon
                              as={TriangleUpIcon}
                              aria-label="sorted ascending"
                              color="gray.400"
                            />
                          )}
                        </chakra.span>
                      ) : null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()} verticalAlign="text-top">
            {rows.map((row) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      paddingX="1"
                      textAlign="center"
                    >
                      {cell.render("Cell")}
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {!isLoading && stats?.length <= 0 && (
          <Text p="8" textAlign="center">
            No Tweets found. Please try a different timeframe
          </Text>
        )}
        {isLoading && (
          <Center pt={10} h="400px">
            <PuffLoader
              color={"hsl(203, 100%, 36%)"}
              loading={isLoading}
              css="display: block; margin: 0 auto; border-color: red;"
              size={50}
            />
          </Center>
        )}
      </Box>
    </Box>
  );
}
